import React, { useState } from "react";
import Card from "components/card";
import InputField from "components/fields/InputField";

const AddFairtradeRCN = () => {
  const initialFormState = {
    date: "",
    truckNumber: "",
    origin: "",
    weighingBridgeWeight: "",
    numberOfBags: "",
    entryMoisture: "",
    pricePerKg: "",
    calibrationMoisture: "",
    dustOffloaded: "",
    dustAfterDrying: "",
    actualCalibratedBags: "",
    actualCalibratedKg: "",
    moistureToAutoclave: "",
  };

  const [formData, setFormData] = useState(initialFormState);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [id]: value
    }));
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await fetch("https://usibras-web-backend.onrender.com/api/addfairtradeRCN", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Something went wrong');
      }
      
      showAlert(`Successfully added! ID: ${data.id}`, 'success');
      setFormData(initialFormState);
    } catch (error) {
      showAlert(error.message || "An error occurred", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mt-5 h-full">
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-xl shadow-xl flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-navy-700 font-medium">Processing...</p>
          </div>
        </div>
      )}

      {alert.show && (
        <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg ${
          alert.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white z-50`}>
          {alert.message}
        </div>
      )}

      <Card extra={"w-full p-4"}>
        <div className="mb-8 w-full">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Add Fair Trade RCN 

          </h4>
          <p className="mt-2 text-base text-gray-600">
            Please fill in all the required information
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Basic Information
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Date*"
                id="date"
                type="date"
                value={formData.date}
                onChange={handleChange}
                required
              />
              <InputField
                label="Truck Number*"
                id="truckNumber"
                type="text"
                placeholder="Enter truck number"
                value={formData.truckNumber}
                onChange={handleChange}
                required
              />
              <InputField
                label="Origin*"
                id="origin"
                type="text"
                placeholder="Enter origin"
                value={formData.origin}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Initial Measurements */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Initial Measurements
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Weighing Bridge Weight*"
                id="weighingBridgeWeight"
                type="number"
                step="0.01"
                placeholder="Enter weight"
                value={formData.weighingBridgeWeight}
                onChange={handleChange}
                required
              />
              <InputField
                label="Number of Bags*"
                id="numberOfBags"
                type="number"
                placeholder="Enter number of bags"
                value={formData.numberOfBags}
                onChange={handleChange}
                required
              />
              <InputField
                label="Price per Kg in Cedis*"
                id="pricePerKg"
                type="number"
                step="0.01"
                placeholder="Enter price per kg"
                value={formData.pricePerKg}
                onChange={handleChange}
                required
              />
              <InputField
                label="Entry Moisture %*"
                id="entryMoisture"
                type="number"
                step="0.01"
                placeholder="Enter entry moisture"
                value={formData.entryMoisture}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Dust Measurements */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Dust Measurements
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Dust Offloaded*"
                id="dustOffloaded"
                type="number"
                step="0.01"
                placeholder="Enter dust offloaded"
                value={formData.dustOffloaded}
                onChange={handleChange}
                required
              />
              <InputField
                label="Dust After Drying*"
                id="dustAfterDrying"
                type="number"
                step="0.01"
                placeholder="Enter dust after drying"
                value={formData.dustAfterDrying}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Calibration Data */}
          <div className="mb-6">
            <h5 className="mb-4 text-lg font-medium text-navy-700 dark:text-white">
              Calibration Data
            </h5>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InputField
                label="Calibration Moisture %*"
                id="calibrationMoisture"
                type="number"
                step="0.01"
                placeholder="Enter calibration moisture"
                value={formData.calibrationMoisture}
                onChange={handleChange}
                required
              />
              <InputField
                label="Actual Calibrated Bags*"
                id="actualCalibratedBags"
                type="number"
                placeholder="Enter actual calibrated bags"
                value={formData.actualCalibratedBags}
                onChange={handleChange}
                required
              />
              <InputField
                label="Actual Calibrated KG*"
                id="actualCalibratedKg"
                type="number"
                step="0.01"
                placeholder="Enter actual calibrated KG"
                value={formData.actualCalibratedKg}
                onChange={handleChange}
                required
              />
              <InputField
                label="Moisture to Autoclave %*"
                id="moistureToAutoclave"
                type="number"
                step="0.01"
                placeholder="Enter moisture to autoclave"
                value={formData.moistureToAutoclave}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => setFormData(initialFormState)}
              className="rounded-xl bg-gray-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-gray-600 active:bg-gray-700 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800 dark:active:bg-navy-900"
              disabled={isLoading}
            >
              Clear
            </button>
            <button
              type="submit"
              className="rounded-xl bg-brand-500 px-5 py-3 text-base font-medium text-white transition duration-200 hover:bg-brand-600 active:bg-brand-700 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-800 dark:active:bg-navy-900 flex items-center justify-center min-w-[120px]"
              disabled={isLoading}
            >
              Submit
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default AddFairtradeRCN; 